import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Material } from '../../types/database';
import { Search, Filter, Video, FileText, Download, Plus, CreditCard as Edit2, Trash2, Link, CheckSquare, Square, X, CheckCircle2, Circle, Lock, Image, Heart, BookOpen, Eye, Layers } from 'lucide-react';
import { MaterialModal } from '../modals/MaterialModal';
import { AssignMaterialModal } from '../modals/AssignMaterialModal';
import { MultipleAssignModal } from '../modals/MultipleAssignModal';
import { CourseSelectionModal } from '../modals/CourseSelectionModal';
import { VideoPlayerModal } from '../modals/VideoPlayerModal';
import { PDFViewerModal } from './PDFViewerModal';
import { HTMLViewerModal } from './HTMLViewerModal';
import { useConfirm } from '../ui/ConfirmDialog';
import { toast } from '../ui/Toaster';
import { EmptyState } from '../ui/EmptyState';
import { CardSkeleton } from '../ui/Skeleton';
import { listMyFavoriteIds, toggleFavorite } from '../../lib/favorites';

export function Library() {
  const confirm = useConfirm();
  const { profile } = useAuth();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [filteredMaterials, setFilteredMaterials] = useState<Material[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMaterialId, setSelectedMaterialId] = useState<string | undefined>();
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [materialToAssign, setMaterialToAssign] = useState<{ id: string; title: string } | null>(null);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<string[]>([]);
  const [isMultipleAssignModalOpen, setIsMultipleAssignModalOpen] = useState(false);
  const [isCourseSelectionModalOpen, setIsCourseSelectionModalOpen] = useState(false);
  const [selectedCourseForAssignment, setSelectedCourseForAssignment] = useState<string>('');
  const [selectedCourseName, setSelectedCourseName] = useState<string>('');
  const [assignedMaterialsInCourse, setAssignedMaterialsInCourse] = useState<Set<string>>(new Set());
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<{ url: string; title: string; embedCode?: string | null } | null>(null);

  // ── PDF viewer (visor inline, sin descarga) ───────────────────────
  const [isPDFViewerOpen, setIsPDFViewerOpen] = useState(false);
  const [selectedPDF,     setSelectedPDF]     = useState<{ url: string; title: string } | null>(null);

  // ── HTML viewer (guías interactivas reemplazando PDFs) ─────────────
  const [isHTMLViewerOpen, setIsHTMLViewerOpen] = useState(false);
  const [selectedHTML,     setSelectedHTML]     = useState<{ url: string; title: string } | null>(null);

  // ── Favoritos ──────────────────────────────────────────────────────
  //   favoriteIds: Set con los material_id que el usuario marcó.
  //   activeTab: 'all' (todos los materiales) | 'favorites' (solo favoritos).
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [activeTab,   setActiveTab]   = useState<'all' | 'favorites'>('all');

  useEffect(() => {
    loadMaterials();
    loadFavorites();
    // Dep en profile?.id (string), no en profile (objeto) — evita recargar la
    // biblioteca completa cada vez que Supabase refresca el token al volver
    // de otra ventana.
  }, [profile?.id]);

  useEffect(() => {
    filterMaterials();
  }, [materials, searchTerm, selectedCategory, selectedType, activeTab, favoriteIds]);

  /** Carga los IDs de materiales favoritos del usuario actual. */
  const loadFavorites = async () => {
    if (!profile?.id) return;
    const ids = await listMyFavoriteIds();
    setFavoriteIds(ids);
  };

  /**
   * Toggle optimista: actualizamos el Set primero (UI inmediata) y
   * sincronizamos contra la BD. Si la BD falla, revertimos.
   */
  const handleToggleFavorite = async (materialId: string) => {
    const wasFavorite = favoriteIds.has(materialId);
    // Optimista
    setFavoriteIds(prev => {
      const next = new Set(prev);
      if (wasFavorite) next.delete(materialId);
      else             next.add(materialId);
      return next;
    });
    const result = await toggleFavorite(materialId, wasFavorite);
    if (result === null) {
      // Falló — revertir
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (wasFavorite) next.add(materialId);
        else             next.delete(materialId);
        return next;
      });
      toast.error('No se pudo actualizar el favorito');
    }
  };

  useEffect(() => {
    if (selectedCourseForAssignment) {
      loadAssignedMaterialsForCourse(selectedCourseForAssignment);
    }
  }, [selectedCourseForAssignment]);

  const loadMaterials = async () => {
    try {
      if (profile?.role === 'admin') {
        const { data, error } = await supabase
          .from('materials')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        setMaterials(data || []);
      } else if (profile?.role === 'docente') {
        const { data: courses, error: courseError } = await supabase
          .from('courses')
          .select('id')
          .or(`docente_id.eq.${profile.id},secondary_docente_id.eq.${profile.id}`);

        if (courseError) throw courseError;

        const courseIds = courses?.map(c => c.id) || [];

        if (courseIds.length === 0) {
          setMaterials([]);
          return;
        }

        const { data: modules, error: modError } = await supabase
          .from('modules')
          .select('id')
          .in('course_id', courseIds);

        if (modError) throw modError;

        const moduleIds = modules?.map(m => m.id) || [];

        if (moduleIds.length === 0) {
          setMaterials([]);
          return;
        }

        const { data: moduleMaterials, error: mmError } = await supabase
          .from('module_materials')
          .select('material_id')
          .in('module_id', moduleIds);

        if (mmError) throw mmError;

        const materialIds = [...new Set(moduleMaterials?.map(mm => mm.material_id) || [])];

        if (materialIds.length === 0) {
          setMaterials([]);
          return;
        }

        const { data, error } = await supabase
          .from('materials')
          .select('*')
          .in('id', materialIds);

        if (error) throw error;

        const materialsWithAccessibility = (data || []).map(material => ({
          ...material,
          isAccessible: true,
          isNew: false,
          sortDate: new Date(material.created_at),
        }));

        // Orden natural por título (acomoda "Clase 1", "Clase 2", ..., "Clase 11"
        // en el orden esperado en vez de lexicográfico). Si dos títulos son
        // iguales, desempata por fecha de creación más reciente.
        const collator = new Intl.Collator('es', { numeric: true, sensitivity: 'base' });
        materialsWithAccessibility.sort((a, b) => {
          const cmp = collator.compare(a.title || '', b.title || '');
          if (cmp !== 0) return cmp;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });

        setMaterials(materialsWithAccessibility);
      } else {
        const { data: enrollments, error: enrollError } = await supabase
          .from('course_enrollments')
          .select('course_id, courses!inner(status)')
          .eq('alumno_id', profile?.id)
          .eq('status', 'active')
          .eq('courses.status', 'active');

        if (enrollError) throw enrollError;

        const courseIds = enrollments?.map(e => e.course_id) || [];

        if (courseIds.length === 0) {
          setMaterials([]);
          return;
        }

        const { data: modules, error: modError } = await supabase
          .from('modules')
          .select('id, status, start_date')
          .in('course_id', courseIds);

        if (modError) throw modError;

        const now = new Date();
        now.setHours(23, 59, 59, 999);

        const allModules = modules || [];
        const moduleIds = allModules.map(m => m.id);

        if (moduleIds.length === 0) {
          setMaterials([]);
          return;
        }

        const { data: moduleMaterials, error: mmError } = await supabase
          .from('module_materials')
          .select('material_id, module_id, modules!inner(start_date, status)')
          .in('module_id', moduleIds);

        if (mmError) throw mmError;

        const materialIds = [...new Set(moduleMaterials?.map(mm => mm.material_id) || [])];

        if (materialIds.length === 0) {
          setMaterials([]);
          return;
        }

        const { data, error } = await supabase
          .from('materials')
          .select('*')
          .in('id', materialIds)
          .order('created_at', { ascending: false });

        if (error) throw error;

        const threeDaysAgo = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);

        const materialsWithFlags = (data || []).map(material => {
          const relatedModules = moduleMaterials?.filter(mm => mm.material_id === material.id) || [];

          const isAccessible = relatedModules.some(mm => {
            const moduleStartDate = mm.modules?.start_date ? new Date(mm.modules.start_date) : null;
            const isActive = mm.modules?.status === 'active' || mm.modules?.status === 'completed';
            return moduleStartDate && moduleStartDate <= now && isActive;
          });

          const isNew = relatedModules.some(mm => {
            const moduleStartDate = mm.modules?.start_date ? new Date(mm.modules.start_date) : null;
            return moduleStartDate && moduleStartDate >= threeDaysAgo && moduleStartDate <= now && mm.modules?.status === 'active';
          });

          // Get the oldest module date for accessible materials
          // or the closest future module date for blocked materials
          let sortDate: Date | null = null;

          if (isAccessible) {
            // For accessible materials, find the oldest module date
            const accessibleModules = relatedModules.filter(mm => {
              const moduleStartDate = mm.modules?.start_date ? new Date(mm.modules.start_date) : null;
              const isActive = mm.modules?.status === 'active' || mm.modules?.status === 'completed';
              return moduleStartDate && moduleStartDate <= now && isActive;
            });

            if (accessibleModules.length > 0) {
              const dates = accessibleModules
                .map(mm => mm.modules?.start_date ? new Date(mm.modules.start_date) : null)
                .filter((d): d is Date => d !== null);

              if (dates.length > 0) {
                sortDate = new Date(Math.min(...dates.map(d => d.getTime())));
              }
            }
          } else {
            // For blocked materials, find the closest future module date
            const futureModules = relatedModules.filter(mm => {
              const moduleStartDate = mm.modules?.start_date ? new Date(mm.modules.start_date) : null;
              return moduleStartDate && moduleStartDate > now;
            });

            if (futureModules.length > 0) {
              const dates = futureModules
                .map(mm => mm.modules?.start_date ? new Date(mm.modules.start_date) : null)
                .filter((d): d is Date => d !== null);

              if (dates.length > 0) {
                sortDate = new Date(Math.min(...dates.map(d => d.getTime())));
              }
            }
          }

          return {
            ...material,
            isNew,
            isAccessible,
            sortDate
          };
        });

        // Sort materials chronologically by module date, with natural sort
        // por título como tiebreaker (así "Clase 1, 2, 11" quedan en orden).
        const collator = new Intl.Collator('es', { numeric: true, sensitivity: 'base' });
        const sortedMaterials = materialsWithFlags.sort((a, b) => {
          // First, separate accessible from blocked
          if (a.isAccessible !== b.isAccessible) {
            return a.isAccessible ? -1 : 1; // Accessible first
          }

          // Within each group, sort by module date
          if (a.sortDate && b.sortDate) {
            const dt = a.sortDate.getTime() - b.sortDate.getTime();
            if (dt !== 0) return dt; // Oldest first
          }

          // Materials without date go to the end
          if (a.sortDate && !b.sortDate) return -1;
          if (!a.sortDate && b.sortDate) return 1;

          // Tiebreaker: orden natural por título (1, 2, ..., 11, 12)
          return collator.compare(a.title || '', b.title || '');
        });

        setMaterials(sortedMaterials);
      }
    } catch (error) {
      console.error('Error loading materials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileUrl: string, fileName: string, fileType: string) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract real file extension from URL instead of using file_type
      const urlPath = fileUrl.split('?')[0];
      const urlExt = urlPath.split('.').pop()?.toLowerCase();
      let downloadName = fileName;

      if (urlExt && ['png', 'jpg', 'jpeg', 'pdf', 'docx', 'xlsx', 'pptx', 'zip', 'rar', 'rvt', 'rfa', 'rte', 'dwg'].includes(urlExt)) {
        downloadName = `${fileName}.${urlExt}`;
      } else if (fileType === 'pdf') {
        downloadName = `${fileName}.pdf`;
      } else {
        downloadName = `${fileName}.${fileType}`;
      }

      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Error al descargar el archivo');
    }
  };

  const filterMaterials = () => {
    let filtered = materials;

    // Pestaña Favoritos: aplicar PRIMERO (afecta a todo lo demás)
    if (activeTab === 'favorites') {
      filtered = filtered.filter((m) => favoriteIds.has(m.id));
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (m) =>
          m.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          m.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((m) => m.category === selectedCategory);
    }

    if (selectedType !== 'all') {
      filtered = filtered.filter((m) => m.file_type === selectedType);
    }

    setFilteredMaterials(filtered);
  };

  const categories = Array.from(new Set(materials.map((m) => m.category).filter(Boolean)));
  const types = Array.from(new Set(materials.map((m) => m.file_type)));

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'video':
        return Video;
      case 'html_guide':
        return Layers;
      case 'pdf':
      case 'document':
      case 'guide':
        return FileText;
      case 'image':
        return Image;
      default:
        return Download;
    }
  };

  const getTypeColor = (fileType: string) => {
    switch (fileType) {
      case 'video':
        return 'bg-red-500/10 text-red-500';
      case 'pdf':
        return 'bg-blue-500/10 text-blue-500';
      case 'document':
        return 'bg-green-500/10 text-green-500';
      case 'guide':
        return 'bg-purple-500/10 text-purple-500';
      case 'image':
        return 'bg-orange-500/10 text-orange-500';
      default:
        return 'bg-gray-500/10 text-subtle';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div>
          <h1 className="text-display text-3xl font-bold text-white mb-2">Biblioteca</h1>
          <p className="text-muted">Cargando materiales…</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  const handleDelete = async (materialId: string) => {
    const ok = await confirm({
      title: 'Eliminar material',
      description: '¿Seguro que deseas eliminar este material? Se perderán también sus asignaciones a módulos.',
      confirmText: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      const { error } = await supabase
        .from('materials')
        .delete()
        .eq('id', materialId);

      if (error) throw error;
      toast.success('Material eliminado');
      loadMaterials();
    } catch (error: any) {
      console.error('Error deleting material:', error);
      toast.error('No se pudo eliminar el material');
    }
  };

  const handleAssignMaterial = (material: Material) => {
    setMaterialToAssign({ id: material.id, title: material.title });
    setIsAssignModalOpen(true);
  };

  const toggleMaterialSelection = (materialId: string) => {
    setSelectedMaterialIds((prev) =>
      prev.includes(materialId)
        ? prev.filter((id) => id !== materialId)
        : [...prev, materialId]
    );
  };

  const handleMultipleAssign = () => {
    if (selectedMaterialIds.length === 0) {
      toast.error('Selecciona al menos un material');
      return;
    }
    setIsMultipleAssignModalOpen(true);
  };

  const handleEnterMultiSelectMode = () => {
    setIsCourseSelectionModalOpen(true);
  };

  const handleCourseSelected = async (courseId: string) => {
    setSelectedCourseForAssignment(courseId);

    try {
      const { data, error } = await supabase
        .from('courses')
        .select('name')
        .eq('id', courseId)
        .single();

      if (error) throw error;
      if (data) {
        setSelectedCourseName(data.name);
      }
    } catch (error) {
      console.error('Error loading course name:', error);
    }

    setIsMultiSelectMode(true);
  };

  const loadAssignedMaterialsForCourse = async (courseId: string) => {
    if (!courseId) {
      setAssignedMaterialsInCourse(new Set());
      return;
    }

    try {
      const { data: modules, error: modError } = await supabase
        .from('modules')
        .select('id')
        .eq('course_id', courseId);

      if (modError) throw modError;

      const moduleIds = modules?.map(m => m.id) || [];

      if (moduleIds.length === 0) {
        setAssignedMaterialsInCourse(new Set());
        return;
      }

      const { data: assignments, error: assignError } = await supabase
        .from('module_materials')
        .select('material_id')
        .in('module_id', moduleIds);

      if (assignError) throw assignError;

      const assignedIds = new Set(assignments?.map(a => a.material_id) || []);
      setAssignedMaterialsInCourse(assignedIds);
    } catch (error) {
      console.error('Error loading assigned materials:', error);
      setAssignedMaterialsInCourse(new Set());
    }
  };

  const exitMultiSelectMode = () => {
    setIsMultiSelectMode(false);
    setSelectedMaterialIds([]);
    setSelectedCourseForAssignment('');
    setSelectedCourseName('');
    setAssignedMaterialsInCourse(new Set());
  };

  const handleClearAllAssignments = async () => {
    if (!selectedCourseForAssignment) return;

    const ok = await confirm({
      title: 'Desasignar todos los materiales',
      description: `Se eliminarán TODAS las asignaciones de materiales de TODOS los módulos del curso “${selectedCourseName}”. Esta acción no se puede deshacer.`,
      confirmText: 'Desasignar todo',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      const { data: modules, error: modError } = await supabase
        .from('modules')
        .select('id')
        .eq('course_id', selectedCourseForAssignment);

      if (modError) throw modError;

      if (modules && modules.length > 0) {
        const moduleIds = modules.map(m => m.id);

        const { error: deleteError } = await supabase
          .from('module_materials')
          .delete()
          .in('module_id', moduleIds);

        if (deleteError) throw deleteError;

        toast.success('Asignaciones eliminadas del curso');
        await loadAssignedMaterialsForCourse(selectedCourseForAssignment);
        loadMaterials();
      }
    } catch (error) {
      console.error('Error clearing assignments:', error);
      toast.error('Error al eliminar las asignaciones');
    }
  };

  const handleVideoClick = (material: Material) => {
    setSelectedVideo({
      url: material.file_url,
      title: material.title,
      embedCode: material.embed_code
    });
    setIsVideoModalOpen(true);
  };

  const handleCloseVideoModal = () => {
    setIsVideoModalOpen(false);
    setSelectedVideo(null);
  };

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-display text-3xl font-bold text-white mb-2">Biblioteca Digital</h1>
          <p className="text-muted">
            {isMultiSelectMode
              ? (
                <span>
                  Curso: <span className="text-cubus-lime font-semibold">{selectedCourseName}</span>
                  {' '}- {selectedMaterialIds.length} material{selectedMaterialIds.length !== 1 ? 'es' : ''} seleccionado{selectedMaterialIds.length !== 1 ? 's' : ''}
                </span>
              )
              : 'Accede a todos los materiales del curso'}
          </p>
        </div>
        {profile?.role === 'admin' && (
          <div className="flex gap-3">
            {isMultiSelectMode ? (
              <>
                <button
                  onClick={handleClearAllAssignments}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg transition-all font-medium"
                >
                  <Trash2 className="w-5 h-5" />
                  Limpiar Curso
                </button>
                <button
                  onClick={exitMultiSelectMode}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all font-medium"
                >
                  <X className="w-5 h-5" />
                  Cancelar
                </button>
                <button
                  onClick={handleMultipleAssign}
                  disabled={selectedMaterialIds.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-cubus-lime text-cubus-bg rounded-lg hover:bg-cubus-lime-dim transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Link className="w-5 h-5" />
                  Asignar {selectedMaterialIds.length > 0 ? `(${selectedMaterialIds.length})` : ''}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleEnterMultiSelectMode}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all font-medium"
                >
                  <CheckSquare className="w-5 h-5" />
                  Asignación Múltiple
                </button>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-cubus-lime text-cubus-bg rounded-lg hover:bg-cubus-lime-dim transition-all font-semibold"
                >
                  <Plus className="w-5 h-5" />
                  Nuevo Material
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Pestañas: Todos / Favoritos.
          Independiente de los filtros (Categorías, Tipos). El usuario puede
          combinarlas — ej. ver favoritos filtrados por tipo PDF. */}
      <div className="flex items-center gap-2 border-b border-white/[0.06] pb-3">
        <button
          onClick={() => setActiveTab('all')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'all'
              ? 'bg-cubus-lime/15 text-cubus-lime border border-cubus-lime/40'
              : 'text-white/60 hover:text-white hover:bg-white/[0.04] border border-transparent'
          }`}
        >
          <BookOpen className="w-4 h-4" />
          Todos
          <span className="text-xs opacity-60">({materials.length})</span>
        </button>
        <button
          onClick={() => setActiveTab('favorites')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'favorites'
              ? 'bg-rose-500/15 text-rose-400 border border-rose-500/40'
              : 'text-white/60 hover:text-white hover:bg-white/[0.04] border border-transparent'
          }`}
        >
          <Heart className={`w-4 h-4 ${activeTab === 'favorites' ? 'fill-current' : ''}`} />
          Favoritos
          <span className="text-xs opacity-60">({favoriteIds.size})</span>
        </button>
      </div>

      <div className="glass-surface rounded-xl p-6 border border-white/[0.06]">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted" />
            <input
              type="text"
              placeholder="Buscar materiales..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 glass-deep border border-white/[0.08] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cubus-lime focus:border-transparent"
            />
          </div>

          <div className="flex gap-3">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted pointer-events-none" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="pl-10 pr-10 py-3 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime appearance-none cursor-pointer"
              >
                <option value="all">Todas las categorías</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-4 py-3 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime appearance-none cursor-pointer"
            >
              <option value="all">Todos los tipos</option>
              {types.map((type) => (
                <option key={type} value={type}>
                  {type.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredMaterials.length === 0 ? (
          <div className="col-span-full">
            <EmptyState
              icon={FileText}
              title="No se encontraron materiales"
              description={searchTerm || selectedCategory !== 'all' || selectedType !== 'all'
                ? 'Ajusta los filtros o el término de búsqueda para ver más resultados.'
                : 'Aún no hay materiales en la biblioteca.'}
            />
          </div>
        ) : (
          filteredMaterials.map((material) => {
            const Icon = getFileIcon(material.file_type);
            const isSelected = selectedMaterialIds.includes(material.id);
            const isBlocked = profile?.role === 'alumno' && material.isAccessible === false;
            return (
              <div
                key={material.id}
                className={`glass-surface rounded-xl overflow-hidden border transition-all duration-200 group relative flex flex-col h-full ${
                  isSelected
                    ? 'border-cubus-lime ring-2 ring-cubus-lime/50'
                    : 'border-white/[0.06] hover:border-cubus-lime/40'
                } ${isBlocked ? 'opacity-[0.08] grayscale cursor-not-allowed' : ''}`}
                onClick={isMultiSelectMode && !isBlocked ? () => toggleMaterialSelection(material.id) : undefined}
                title={isBlocked ? 'Disponible cuando el módulo esté activo' : ''}
              >
                <div className="relative aspect-video glass-deep overflow-hidden">
                  {isBlocked && profile?.role === 'alumno' && (
                    <div className="absolute top-2 right-2 z-10">
                      <div className="w-6 h-6 bg-gray-800/80 rounded-full flex items-center justify-center backdrop-blur-sm">
                        <Lock className="w-3 h-3 text-muted" />
                      </div>
                    </div>
                  )}
                  {/* Botón corazón (favoritos). Esquina superior derecha,
                      bajo el candado si está bloqueado. Funciona en multi-select
                      mode también (no marca el material). */}
                  {!isBlocked && !isMultiSelectMode && (() => {
                    const isFav = favoriteIds.has(material.id);
                    return (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFavorite(material.id);
                        }}
                        title={isFav ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                        aria-label={isFav ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                        className={`absolute top-2 right-2 z-10 w-8 h-8 rounded-full backdrop-blur-md border flex items-center justify-center transition-all ${
                          isFav
                            ? 'bg-rose-500/25 border-rose-400/60 text-rose-400 hover:bg-rose-500/40'
                            : 'bg-black/40 border-white/20 text-white/70 hover:bg-rose-500/15 hover:border-rose-400/40 hover:text-rose-400'
                        }`}
                      >
                        <Heart className={`w-4 h-4 ${isFav ? 'fill-current' : ''}`} />
                      </button>
                    );
                  })()}
                  {isMultiSelectMode && !isBlocked && (
                    <div className="absolute top-2 left-2 z-10">
                      {isSelected ? (
                        <div className="w-6 h-6 bg-cubus-lime rounded border-2 border-cubus-lime flex items-center justify-center">
                          <CheckSquare className="w-5 h-5 text-cubus-bg" />
                        </div>
                      ) : (
                        <div className="w-6 h-6 glass-deep rounded border-2 border-white/[0.1] flex items-center justify-center">
                          <Square className="w-4 h-4 text-faint" />
                        </div>
                      )}
                    </div>
                  )}
                  {material.thumbnail_url ? (
                    // Las guías HTML usan cover.png con branding (logo + título
                    // grande) — se ven mal a full-bleed. Usamos object-contain
                    // con padding sobre fondo oscuro, como las miniaturas de video.
                    material.file_type === 'html_guide' ? (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-cubus-deep to-cubus-bg p-3">
                        <img
                          src={material.thumbnail_url}
                          alt={material.title}
                          loading="lazy"
                          className="max-w-full max-h-full object-contain rounded-md shadow-lg group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    ) : (
                      <img
                        src={material.thumbnail_url}
                        alt={material.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    )
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon className="w-12 h-12 text-faint" />
                    </div>
                  )}
                </div>

                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1">
                      <h3 className="font-bold text-white line-clamp-2 group-hover:text-cubus-lime transition-colors">
                        {material.title}
                      </h3>
                      {material.isNew && profile?.role === 'alumno' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 mt-1 rounded-md text-xs font-semibold bg-cubus-lime/20 text-cubus-lime border border-cubus-lime/30 animate-pulse">
                          ✨ Recién Cargado
                        </span>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium whitespace-nowrap ${getTypeColor(material.file_type)}`}>
                      {material.file_type.toUpperCase()}
                    </span>
                  </div>
                  {material.description && (
                    <p className="text-sm text-muted mb-4 line-clamp-2">{material.description}</p>
                  )}

                  {/* mt-auto empuja los botones al fondo de la card —
                      así quedan alineados aunque títulos/descripciones
                      varíen en alto. */}
                  <div className="space-y-2 mt-auto">
                    {isMultiSelectMode && selectedCourseForAssignment ? (
                      assignedMaterialsInCourse.has(material.id) ? (
                        <div className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-green-500/10 text-green-400 rounded-lg border border-green-500/30 font-medium">
                          <CheckCircle2 className="w-4 h-4" />
                          Ya Asignado
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-gray-700/50 text-muted rounded-lg border border-white/[0.1]/30 font-medium">
                          <Circle className="w-4 h-4" />
                          Por Asignar
                        </div>
                      )
                    ) : !isMultiSelectMode && !isBlocked ? (
                      material.file_type === 'html_guide' ? (
                        // Guía HTML interactiva (reemplaza PDFs). Abre en
                        // modal full-screen con iframe sandbox.
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedHTML({ url: material.file_url, title: material.title });
                            setIsHTMLViewerOpen(true);
                          }}
                          className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-cubus-lime/10 hover:bg-cubus-lime text-cubus-lime hover:text-cubus-bg rounded-lg transition-all duration-200 font-medium"
                        >
                          <Layers className="w-4 h-4" />
                          Ver Guía
                        </button>
                      ) : material.file_type === 'video' ? (
                        // Cualquier rol: video abre en modal (no en Vimeo nueva
                        // pestaña). Antes el docente caía en window.open porque
                        // la condición incluía `&& profile?.role === 'alumno'`.
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVideoClick(material);
                          }}
                          className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-cubus-lime/10 hover:bg-cubus-lime text-cubus-lime hover:text-cubus-bg rounded-lg transition-all duration-200 font-medium"
                        >
                          <Video className="w-4 h-4" />
                          Ver Video
                        </button>
                      ) : material.file_type === 'pdf' ? (
                        // PDFs: NO se descargan, se ven en visor inline con
                        // bloqueo máximo (sin botón save/print, watermark, etc.).
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPDF({ url: material.file_url, title: material.title });
                            setIsPDFViewerOpen(true);
                          }}
                          className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-cubus-lime/10 hover:bg-cubus-lime text-cubus-lime hover:text-cubus-bg rounded-lg transition-all duration-200 font-medium"
                        >
                          <Eye className="w-4 h-4" />
                          Ver PDF
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isMultiSelectMode) {
                              e.preventDefault();
                            } else {
                              handleDownload(material.file_url, material.title, material.file_type);
                            }
                          }}
                          className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-cubus-lime/10 hover:bg-cubus-lime text-cubus-lime hover:text-cubus-bg rounded-lg transition-all duration-200 font-medium"
                        >
                          <Download className="w-4 h-4" />
                          Descargar
                        </button>
                      )
                    ) : !isMultiSelectMode && isBlocked ? (
                      <div className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-gray-800/50 text-subtle rounded-lg border border-white/[0.08]/30 font-medium cursor-not-allowed">
                        <Lock className="w-4 h-4" />
                        Bloqueado
                      </div>
                    ) : null}

                    {profile?.role === 'admin' && !isMultiSelectMode && (
                      <div className="space-y-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAssignMaterial(material);
                          }}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-cubus-lime/10 text-cubus-lime rounded-lg hover:bg-cubus-lime/20 transition-all text-sm font-medium"
                        >
                          <Link className="w-4 h-4" />
                          Asignar a Curso
                        </button>

                        {profile?.role === 'admin' && (
                          <div className="flex gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedMaterialId(material.id);
                                setIsModalOpen(true);
                              }}
                              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-all text-sm"
                            >
                              <Edit2 className="w-4 h-4" />
                              Editar
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(material.id);
                              }}
                              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-all text-sm"
                            >
                              <Trash2 className="w-4 h-4" />
                              Eliminar
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {filteredMaterials.length > 0 && (
        <div className="text-center text-muted text-sm">
          Mostrando {filteredMaterials.length} de {materials.length} materiales
        </div>
      )}

      <MaterialModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedMaterialId(undefined);
        }}
        onSuccess={loadMaterials}
        materialId={selectedMaterialId}
      />

      {materialToAssign && (
        <AssignMaterialModal
          isOpen={isAssignModalOpen}
          onClose={() => {
            setIsAssignModalOpen(false);
            setMaterialToAssign(null);
          }}
          onSuccess={() => {
            loadMaterials();
          }}
          materialId={materialToAssign.id}
          materialTitle={materialToAssign.title}
        />
      )}

      <MultipleAssignModal
        isOpen={isMultipleAssignModalOpen}
        onClose={() => {
          setIsMultipleAssignModalOpen(false);
        }}
        onSuccess={() => {
          loadMaterials();
          exitMultiSelectMode();
        }}
        selectedMaterialIds={selectedMaterialIds}
        materialTitles={materials
          .filter((m) => selectedMaterialIds.includes(m.id))
          .map((m) => m.title)}
        onCourseChange={loadAssignedMaterialsForCourse}
      />

      <CourseSelectionModal
        isOpen={isCourseSelectionModalOpen}
        onClose={() => setIsCourseSelectionModalOpen(false)}
        onSelectCourse={handleCourseSelected}
      />

      {selectedVideo && (
        <VideoPlayerModal
          isOpen={isVideoModalOpen}
          onClose={handleCloseVideoModal}
          videoUrl={selectedVideo.url}
          videoTitle={selectedVideo.title}
          embedCode={selectedVideo.embedCode}
        />
      )}

      <PDFViewerModal
        open={isPDFViewerOpen}
        url={selectedPDF?.url ?? null}
        title={selectedPDF?.title ?? ''}
        onClose={() => { setIsPDFViewerOpen(false); setSelectedPDF(null); }}
      />

      <HTMLViewerModal
        open={isHTMLViewerOpen}
        url={selectedHTML?.url ?? null}
        title={selectedHTML?.title ?? ''}
        onClose={() => { setIsHTMLViewerOpen(false); setSelectedHTML(null); }}
      />
    </div>
  );
}
